import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import GoogleCoordinatesAutocomplete from '../../components/hospitalOnboarding/GoogleCoordinatesAutocomplete';
import {
  Mic,
  Building2,
  MapPin,
  Hash,
  Bed,
  Link2,
  Map,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Check
} from 'lucide-react';

const HOSPITAL_TYPES = [
  'Multi-speciality',
  'Trauma Center',
  'Cardiac Hospital',
  'Mother & Child',
  'Nursing Home',
  'Diagnostic / Day Care',
  'Cancer / Oncology',
  'Orthopaedic'
];

export const Step1HospitalProfile = () => {
  const navigate = useNavigate();
  const { formData, updateFields, saveAndContinue, saveAndExit, isSaving } = useHospitalOnboarding();
  const [errors, setErrors] = useState({});

  const handleTypeToggle = (type) => {
    const currentTypes = formData.hospital_types || [];
    const updated = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    updateFields({ hospital_types: updated });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.hospital_name?.trim()) newErrors.hospital_name = 'Hospital name is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state?.trim()) newErrors.state = 'State is required';
    if (formData.pincode && !/^\d{6}$/.test(formData.pincode.trim())) {
      newErrors.pincode = 'PIN code must be exactly 6 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice matching Image 2 */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 1 OF 5
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Hospital profile
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal">
            Tell us about your hospital and its location.
          </p>
          {/* Smart Ambulance Blue accent underline */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=hospital&step=1')}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-xs font-medium text-slate-700 shadow-2xs transition-all cursor-pointer self-start sm:self-auto"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      {/* Main Form Card: Hospital Details matching Image 2 */}
      <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 shadow-xs p-6 sm:p-8 mb-6">
        {/* Section Header with Blue Icon */}
        <div className="flex items-center gap-3.5 mb-6 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800 leading-tight">
              Hospital details
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 font-normal">
              Basic information about your hospital and location.
            </p>
          </div>
        </div>

        <form onSubmit={handleContinue} className="space-y-5">
          {/* Hospital Name */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Hospital Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter hospital name"
              value={formData.hospital_name || ''}
              onChange={(e) => {
                updateFields({ hospital_name: e.target.value });
                if (errors.hospital_name) setErrors((prev) => ({ ...prev, hospital_name: '' }));
              }}
              className={`w-full px-4 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                errors.hospital_name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
              }`}
            />
            {errors.hospital_name && (
              <p className="text-[11px] text-red-600 mt-1">{errors.hospital_name}</p>
            )}
          </div>

          {/* Hospital Type Pills with checkboxes */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-2">
              Hospital Type
            </label>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              {HOSPITAL_TYPES.map((type) => {
                const isSelected = (formData.hospital_types || []).includes(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTypeToggle(type)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs transition-all select-none cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 border-blue-400 text-blue-700 font-medium shadow-2xs'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 font-normal'
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </span>
                    <span>{type}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* City, State & PIN Code (Inputs with icons inside) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                City <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Enter city"
                  value={formData.city || ''}
                  onChange={(e) => {
                    updateFields({ city: e.target.value });
                    if (errors.city) setErrors((prev) => ({ ...prev, city: '' }));
                  }}
                  className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                    errors.city ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                  }`}
                />
              </div>
              {errors.city && (
                <p className="text-[11px] text-red-600 mt-1">{errors.city}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                State <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Enter state"
                  value={formData.state || ''}
                  onChange={(e) => {
                    updateFields({ state: e.target.value });
                    if (errors.state) setErrors((prev) => ({ ...prev, state: '' }));
                  }}
                  className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                    errors.state ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                  }`}
                />
              </div>
              {errors.state && (
                <p className="text-[11px] text-red-600 mt-1">{errors.state}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                PIN Code <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  maxLength={6}
                  placeholder="Enter PIN code"
                  value={formData.pincode || ''}
                  onChange={(e) => {
                    updateFields({ pincode: e.target.value.replace(/\D/g, '') });
                    if (errors.pincode) setErrors((prev) => ({ ...prev, pincode: '' }));
                  }}
                  className={`w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                    errors.pincode ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                  }`}
                />
              </div>
              {errors.pincode && (
                <p className="text-[11px] text-red-600 mt-1">{errors.pincode}</p>
              )}
            </div>
          </div>

          {/* No. of Beds & Google Coordinates (Inputs with icons inside) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                No. of Beds
              </label>
              <div className="relative">
                <Bed className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="number"
                  min="0"
                  placeholder="Enter number of beds"
                  value={formData.number_of_beds || ''}
                  onChange={(e) => updateFields({ number_of_beds: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Location
              </label>
              <GoogleCoordinatesAutocomplete
                value={formData.google_coordinates || ''}
                onChange={(coords) => updateFields({ google_coordinates: coords })}
                placeholder="Search place or enter location coordinates"
              />
            </div>
          </div>

          {/* Google Maps Link (Hidden as requested) */}
          {/*
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Google Maps Link
            </label>
            <div className="relative">
              <Link2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="url"
                placeholder="Enter Google Maps link"
                value={formData.google_maps_link || ''}
                onChange={(e) => updateFields({ google_maps_link: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>
          </div>
          */}

          {/* Full Address */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Full Address
            </label>
            <div className="relative">
              <Map className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
              <textarea
                rows={3}
                placeholder="Enter full address"
                value={formData.full_address || ''}
                onChange={(e) => updateFields({ full_address: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all resize-y min-h-[72px]"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Bottom Action Buttons matching Image 2 */}
      <div className="flex items-center justify-between pt-2 pb-12">
        <button
          type="button"
          onClick={() => {
            if (typeof window !== 'undefined' && window.__ccOnboardingBack) {
              window.__ccOnboardingBack();
              return;
            }
            navigate('/hospital/onboarding');
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={saveAndExit}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-2xs"
          >
            <Bookmark className="w-4 h-4 text-slate-400" />
            <span>Save & exit</span>
          </button>

          <button
            type="button"
            onClick={handleContinue}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-xs transition-colors cursor-pointer"
          >
            <span>Save & continue</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step1HospitalProfile;
